import { useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { Mail, X } from 'lucide-react';
import { useAuth } from '../AuthModule';
import {
  API,
  PROFILE_FIELDS,
  DEFAULT_BUBBLE_COLOR_HEX,
  defaultProfile,
  profileToDisplay,
  toColorInputValue,
  normalizeHexTo6,
  getLighterBorderColor,
  getContrastingTextColor,
  AvatarFallback,
} from './profileHelpers';
import AvatarWithDesignerBadge from './AvatarWithDesignerBadge';
import { useT } from '../../i18n/I18nModule';

function RatingCommentRow({ authorName, authorBubbleColor, reason }) {
  const displayName = authorName || '—';
  const hex = normalizeHexTo6(authorBubbleColor) || DEFAULT_BUBBLE_COLOR_HEX;
  const bg = toColorInputValue(hex);
  const textColor = getContrastingTextColor(hex);
  return (
    <>
      <hr />
      <div className="Profile-rating-comment-inner">
        <span
          className="Profile-rating-comment-author-bubble"
          style={{ backgroundColor: bg, color: textColor }}
        >
          {displayName}
        </span>
        <span className="Profile-rating-comment-text">{reason || '(ei kommenttia)'}</span>
      </div>
    </>
  );
}

export default function PublicProfileView({ displayName, redirect }) {
  const { user } = useAuth();
  const t = useT();
  const [owner, setOwner] = useState(null); // { username, displayName }
  const [profile, setProfile] = useState(null);
  const [bubbleColor, setBubbleColor] = useState(DEFAULT_BUBBLE_COLOR_HEX);
  const [profileUserId, setProfileUserId] = useState(null);
  const [authoredEventsData, setAuthoredEventsData] = useState(null);
  const [hostedRatingsData, setHostedRatingsData] = useState(null);
  const [avatarImageError, setAvatarImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false); // Avatar not found
  const [descriptionHeader, setDescriptionHeader] = useState('');
  const [descriptionBody, setDescriptionBody] = useState('');
  const [isDmOpen, setIsDmOpen] = useState(false);
  const [dmMessage, setDmMessage] = useState('');
  const [dmSending, setDmSending] = useState(false);
  const [dmStatus, setDmStatus] = useState(null);
  const [avatarDesigner, setAvatarDesigner] = useState(null);
  /** null = loading; object when loaded (visitor + logged in only) */
  const [friendState, setFriendState] = useState(null);
  const [friendActionError, setFriendActionError] = useState(null);
  const [friendActionBusy, setFriendActionBusy] = useState(false);
  const [incomingDecision, setIncomingDecision] = useState('');
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);
  const [ignoreReasonDraft, setIgnoreReasonDraft] = useState('');

  useEffect(() => {
    if (!displayName) return;
    setLoading(true);
    setNotFound(false);
    fetch(`${API}/api/profile/by-display-name/${encodeURIComponent(displayName)}`)
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true);
          setAvatarDesigner(null);
          return null;
        }
        if (!res.ok) {
          throw new Error('Profile load failed');
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;
        const p = data.profile && typeof data.profile === 'object' ? data.profile : null;
        setProfile(p);
        setOwner({ username: data.username, displayName: data.displayName });
        setProfileUserId(data.userId);
        setAvatarDesigner(data.avatarDesigner ?? null);
        const hex = normalizeHexTo6(data.bubbleColor);
        setBubbleColor(hex || DEFAULT_BUBBLE_COLOR_HEX);
        setAvatarImageError(false);
      })
      .catch(() => {
        setProfile(null);
        setOwner(null);
        setProfileUserId(null);
        setAvatarDesigner(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [displayName]);

  useEffect(() => {
    const base = { ...defaultProfile(), ...profile };
    if (!profileUserId || (base.showEventsHosted !== 'YES' && base.showEventRatings !== 'YES')) {
      setAuthoredEventsData({ count: 0, events: [] });
      return;
    }
    fetch(`${API}/api/users/${encodeURIComponent(profileUserId)}/authored-events`)
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.count === 'number' && Array.isArray(data.events)) {
          setAuthoredEventsData({ count: data.count, events: data.events });
        } else {
          setAuthoredEventsData({ count: 0, events: [] });
        }
      })
      .catch(() => setAuthoredEventsData({ count: 0, events: [] }));
  }, [profileUserId, profile]);

  useEffect(() => {
    if (!profileUserId || !profile) return;
    const base = { ...defaultProfile(), ...profile };
    if (base.showEventRatings !== 'YES') {
      setHostedRatingsData(null);
      return;
    }
    fetch(`${API}/api/users/${encodeURIComponent(profileUserId)}/hosted-event-ratings`)
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.totalRatings === 'number' && Array.isArray(data.ratings)) {
          setHostedRatingsData({
            totalRatings: data.totalRatings,
            averageRating: data.averageRating ?? 0,
            ratings: data.ratings,
          });
        } else {
          setHostedRatingsData({ totalRatings: 0, averageRating: 0, ratings: [] });
        }
      })
      .catch(() => setHostedRatingsData({ totalRatings: 0, averageRating: 0, ratings: [] }));
  }, [profileUserId, profile]);

  // Load description text from user_settings for the profile owner
  useEffect(() => {
    if (!profileUserId) {
      setDescriptionHeader('');
      setDescriptionBody('');
      return;
    }
    fetch(`${API}/api/users/${encodeURIComponent(profileUserId)}/settings`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((settings) => {
        setDescriptionHeader(settings?.descriptionHeader ?? '');
        setDescriptionBody(settings?.descriptionBody ?? '');
      })
      .catch(() => {
        setDescriptionHeader('');
        setDescriptionBody('');
      });
  }, [profileUserId]);

  const displayProfile = profileToDisplay(profile);

  const currentUserId = user?.id != null ? String(user.id).replace(/n$/, '') : null;
  const profileUserIdStr = profileUserId != null ? String(profileUserId).replace(/n$/, '') : null;
  const isOwner = !!currentUserId && currentUserId === profileUserIdStr;
  const isAdminDmBypassUser = currentUserId === '1';
  const hideFriendRequestUi = profileUserIdStr === '1';

  const loadFriendStatus = useCallback(() => {
    const viewer = user?.id != null ? String(user.id).replace(/n$/, '') : null;
    const other = profileUserId != null ? String(profileUserId).replace(/n$/, '') : null;
    if (!viewer || !other || viewer === other) {
      setFriendState({ state: 'self' });
      return;
    }
    setFriendState(null);
    setFriendActionError(null);
    fetch(
      `${API}/api/user-friends/status?userId=${encodeURIComponent(viewer)}&otherUserId=${encodeURIComponent(other)}`,
    )
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        setFriendState({
          state: data.state || 'none',
          ignoreReason: data.ignoreReason,
        });
        setIncomingDecision('');
      })
      .catch(() => {
        setFriendState({ state: 'none' });
      });
  }, [user?.id, profileUserId]);

  useEffect(() => {
    if (!user?.id) {
      setFriendState(null);
      return;
    }
    if (!profileUserIdStr || isOwner) {
      setFriendState({ state: 'self' });
      return;
    }
    loadFriendStatus();
  }, [user?.id, profileUserIdStr, isOwner, loadFriendStatus]);

  const renderStaticValue = (field, value) => {
    // Special rendering for contact email
    if (field.key === 'contactEmail') {
      const email = value && String(value).trim();
      if (!email) return '—';
      return (
        <a href={`mailto:${email}`}>
          {email}
        </a>
      );
    }
    // Special rendering for website
    if (field.key === 'website') {
      const rawUrl = value && String(value).trim();
      if (!rawUrl) return '—';
      const href = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {rawUrl}
        </a>
      );
    }
    if (field.type === 'yesno' || field.type === 'image') return value === 'YES' ? 'Kyllä' : 'Ei';
    return value ?? '—';
  };

  const handleOpenDm = () => {
    if (!user || !currentUserId || !profileUserIdStr || currentUserId === profileUserIdStr) return;
    setIsDmOpen(true);
    setDmStatus(null);
  };

  const handleCancelDm = () => {
    setIsDmOpen(false);
    setDmMessage('');
    setDmStatus(null);
  };

  const handleSendDm = async () => {
    if (!dmMessage.trim() || !currentUserId || !profileUserIdStr) return;
    setDmSending(true);
    try {
      const res = await fetch(`${API}/api/direct-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUserId,
          fromUserId: currentUserId,
          toUserId: profileUserIdStr,
          content: dmMessage.trim(),
        }),
      });
      if (!res.ok) {
        throw new Error('Failed to send direct message');
      }
      setDmStatus({ variant: 'success', message: 'Viesti lähetetty.' });
      setDmMessage('');
      setIsDmOpen(false);
    } catch (e) {
      setDmStatus({ variant: 'error', message: 'Viestin lähetys epäonnistui.' });
    } finally {
      setDmSending(false);
    }
  };

  const handleMessageButtonClick = () => {
    if (!isDmOpen) {
      handleOpenDm();
    } else {
      handleSendDm();
    }
  };

  const sendFriendRequest = async () => {
    if (!currentUserId || !profileUserIdStr) return;
    setFriendActionBusy(true);
    setFriendActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId, targetUserId: profileUserIdStr }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFriendActionError(typeof data.error === 'string' ? data.error : 'Pyyntö epäonnistui.');
        return;
      }
      loadFriendStatus();
    } finally {
      setFriendActionBusy(false);
    }
  };

  const cancelOutgoingRequest = async () => {
    if (!currentUserId || !profileUserIdStr) return;
    setFriendActionBusy(true);
    setFriendActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/request`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId, targetUserId: profileUserIdStr }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setFriendActionError(typeof data.error === 'string' ? data.error : 'Peruutus epäonnistui.');
        return;
      }
      loadFriendStatus();
    } finally {
      setFriendActionBusy(false);
    }
  };

  const submitIncomingApprove = async () => {
    if (!currentUserId || !profileUserIdStr) return;
    setFriendActionBusy(true);
    setFriendActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUserId,
          requesterId: profileUserIdStr,
          action: 'APPROVE',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFriendActionError(typeof data.error === 'string' ? data.error : 'Hyväksyntä epäonnistui.');
        return;
      }
      loadFriendStatus();
    } finally {
      setFriendActionBusy(false);
    }
  };

  const submitIncomingIgnore = async () => {
    const reason = ignoreReasonDraft.trim();
    if (!reason || !currentUserId || !profileUserIdStr) return;
    setFriendActionBusy(true);
    setFriendActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUserId,
          requesterId: profileUserIdStr,
          action: 'IGNORE',
          ignoreReason: reason,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFriendActionError(typeof data.error === 'string' ? data.error : 'Hylkäys epäonnistui.');
        return;
      }
      setIgnoreModalOpen(false);
      setIgnoreReasonDraft('');
      loadFriendStatus();
    } finally {
      setFriendActionBusy(false);
    }
  };

  const removeApprovedFriend = async () => {
    if (!currentUserId || !profileUserIdStr) return;
    const name = owner?.displayName || owner?.username || 'tämä käyttäjä';
    if (!window.confirm(`Poistetaanko ${name} kavereistasi?`)) return;
    setFriendActionBusy(true);
    setFriendActionError(null);
    try {
      const qp = new URLSearchParams({
        userId: currentUserId,
        friendUserId: profileUserIdStr,
      });
      const res = await fetch(`${API}/api/user-friends/approved?${qp.toString()}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId, friendUserId: profileUserIdStr }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFriendActionError(typeof data.error === 'string' ? data.error : 'Kaverin poisto epäonnistui.');
        return;
      }
      loadFriendStatus();
    } finally {
      setFriendActionBusy(false);
    }
  };

  const handleIncomingConfirmClick = () => {
    if (incomingDecision === 'APPROVE') {
      submitIncomingApprove();
    } else if (incomingDecision === 'IGNORE') {
      setIgnoreReasonDraft('');
      setIgnoreModalOpen(true);
    }
  };

  const canShowYvButton =
    !!user?.id &&
    !isOwner &&
    (isAdminDmBypassUser || (friendState && friendState.state === 'approved'));
  const showYvPlaceholderForOwner = !!user?.id && isOwner;

  const hostedEventCount = authoredEventsData?.count ?? 0;
  const hasHostedEventsNoRatings =
    hostedEventCount > 0 &&
    hostedRatingsData != null &&
    hostedRatingsData.totalRatings === 0;
  const hostedEventsVisibleNoRatingsBlock =
    displayProfile.showEventsHosted === 'YES' &&
    displayProfile.showEventRatings !== 'YES' &&
    authoredEventsData != null &&
    hostedEventCount > 0 &&
    (authoredEventsData.events || []).every((ev) => (ev.ratingsCount ?? 0) === 0);

  if (notFound) {
    return (
      <div className="App-block">
        <p>Profiilia ei löydy.</p>
      </div>
    );
  }

  const redirectPath = redirect && String(redirect).trim().startsWith('/') ? redirect.trim() : null;

  return (
    <div className="App-block App-block--public-profile">
      <div className="public-profile-view-wrapper">
        {redirectPath && (
          <a href={redirectPath} className="PublicProfile-redirect-close" aria-label="Takaisin">
            <X size={24} />
          </a>
        )}
        {isOwner && !redirectPath && (
          <div className="PublicProfile-back-link">
            <a href="/profiili">← Takaisin hallintanäkymään</a>
          </div>
        )}
        {loading ? (
          <p>Ladataan…</p>
        ) : !owner ? (
          <p>Profiilia ei voida näyttää.</p>
        ) : (
          <>
            <div className="Profile-view">
            <div className="Profile-public-header-row">
              <div className="Profile-avatar-wrap Profile-public-header-left">
                <div className="Profile-field-row">
                  <span className="Profile-field-value">
                    <AvatarWithDesignerBadge designer={avatarDesigner} variant="public">
                      {displayProfile.avatar === 'YES' && profile?.avatar && !avatarImageError ? (
                        <img
                          src={profile.avatar}
                          alt=""
                          className="Profile-avatar Profile-avatar-inline"
                          onError={() => setAvatarImageError(true)}
                        />
                      ) : (
                        <AvatarFallback
                          bubbleColorHex={bubbleColor}
                          displayName={owner?.displayName}
                          username={owner?.username}
                          size="large"
                        />
                      )}
                    </AvatarWithDesignerBadge>
                  </span>
                </div>
                <div
                  className="Profile-bubble-preview"
                  style={{
                    backgroundColor: toColorInputValue(bubbleColor),
                    border: `1px solid ${getLighterBorderColor(bubbleColor)}`,
                  }}
                  aria-hidden
                >
                  MOI!<br /> Minä olen<br /> {owner?.displayName || owner?.username || displayName}
                </div>
              </div>
              {user && user.id && (
                <div className="Profile-public-header-actions Profile-message-button-wrap--inline">
                  {!isOwner && !isAdminDmBypassUser && friendState == null && (
                    <span className="Profile-friend-status Profile-friend-status--loading">Kaveritila…</span>
                  )}
                  {!isOwner && !isAdminDmBypassUser && !hideFriendRequestUi && friendState && friendState.state === 'none' && (
                    <div className="Profile-friend-block">
                      <button
                        type="button"
                        className="Button Button-secondary Profile-friend-request-btn"
                        onClick={sendFriendRequest}
                        disabled={friendActionBusy}
                      >
                        Lähetä kaveripyyntö
                      </button>
                    </div>
                  )}
                  {!isOwner && !isAdminDmBypassUser && friendState && friendState.state === 'outgoing_requested' && (
                    <div className="Profile-friend-block">
                      <p className="Profile-friend-note">Kaveripyyntö lähetetty.</p>
                      <button
                        type="button"
                        className="Button Button-secondary"
                        onClick={cancelOutgoingRequest}
                        disabled={friendActionBusy}
                      >
                        Peruuta pyyntö
                      </button>
                    </div>
                  )}
                  {!isOwner && !isAdminDmBypassUser && friendState && friendState.state === 'incoming_requested' && (
                    <div className="Profile-friend-block Profile-friend-incoming">
                      <label htmlFor="friend-incoming-decision" className="Profile-friend-label">
                        Kaveripyyntö
                      </label>
                      <select
                        id="friend-incoming-decision"
                        className="Profile-friend-select"
                        value={incomingDecision}
                        onChange={(e) => setIncomingDecision(e.target.value)}
                        disabled={friendActionBusy}
                      >
                        <option value="">Valitse…</option>
                        <option value="APPROVE">Hyväksy</option>
                        <option value="IGNORE">Ohita (syy)</option>
                      </select>
                      <button
                        type="button"
                        className="Button"
                        onClick={handleIncomingConfirmClick}
                        disabled={friendActionBusy || !incomingDecision}
                      >
                        Toteuta
                      </button>
                    </div>
                  )}
                  {!isOwner && !isAdminDmBypassUser && friendState && friendState.state === 'ignored' && (
                    <p className="Profile-friend-note">Kaveripyyntö hylätty.</p>
                  )}
                  {!isOwner && !isAdminDmBypassUser && friendState && friendState.state === 'you_ignored_incoming' && (
                    <p className="Profile-friend-note">Ohitit tämän käyttäjän kaveripyynnön.</p>
                  )}
                  {!isOwner && friendState && friendState.state === 'approved' ? (
                    <div className="Profile-friend-block">
                      <button
                        type="button"
                        className="Button Button-secondary Profile-friend-remove-btn"
                        onClick={removeApprovedFriend}
                        disabled={friendActionBusy}
                      >
                        Poista kavereista
                      </button>
                    </div>
                  ) : null}
                  {!isOwner && !isAdminDmBypassUser && friendActionError && (
                    <p className="Profile-friend-error" role="alert">
                      {friendActionError}
                    </p>
                  )}
                  {showYvPlaceholderForOwner ? (
                    <div className="Profile-message-button-wrap Profile-message-button-wrap--inline">
                      <span
                        className="Profile-message-button Profile-message-button--dimmed"
                        aria-disabled="true"
                        title="Näin Lähetä YV -painike näkyy muille käyttäjille"
                      >
                        <Mail className="Profile-message-button-icon" /> {t('profile.sendDm')}
                      </span>
                    </div>
                  ) : null}
                  {canShowYvButton && (
                    <div className="Profile-message-button-wrap Profile-message-button-wrap--inline">
                      <span
                        className="Profile-message-button"
                        role="button"
                        tabIndex={0}
                        onClick={handleMessageButtonClick}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMessageButtonClick();
                          }
                        }}
                      >
                        {isDmOpen ? (
                          'Send'
                        ) : (
                          <>
                            <Mail className="Profile-message-button-icon" /> {t('profile.sendDm')}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
              <div className="Profile-description">
                {descriptionHeader && <h1 className="Profile-description-header">{descriptionHeader}</h1>}
                {descriptionBody && <p>{descriptionBody}</p>}
              </div>

            <div className="user-settings PublicProfile-fields">
              {PROFILE_FIELDS.filter((f) => f.key !== 'avatar' && f.key !== 'allowDms').map((field) => {
                const IconComponent =
                  field.type === 'icon' &&
                  field.label &&
                  LucideIcons[field.label.charAt(0).toUpperCase() + field.label.slice(1)];
                const isVisible = displayProfile[field.visibilityKey] === 'YES';
                if (!isVisible) return null;
                return (
                  <div
                    key={field.key}
                    className="Profile-field Profile-field-row"
                  >
                    <span className="Profile-field-label" aria-label={field.type === 'icon' ? field.key : undefined}>
                      {field.type === 'icon' && IconComponent ? <IconComponent size={18} /> : field.label}
                    </span>
                    <span className="Profile-field-value">
                      {renderStaticValue(field, displayProfile[field.key])}
                    </span>
                  </div>
                );
              })}
            </div>

            {displayProfile.showEventsHosted === 'YES' && (
              <>
                <div className="Profile-field-row Profile-section-row">
                  <h3 className="Profile-section-title">
                    {t('profile.organizedEvents')}
                  </h3>
                </div>
                <hr className="Profile-section-hr" aria-hidden="true" />
                {authoredEventsData && (
                  authoredEventsData.count === 0 ? (
                    <p className="Profile-section-note">{t('profile.noOrganizedEvents')}</p>
                  ) : authoredEventsData.count > 0 ? (
                    <p className="Profile-section-note">
                      ({authoredEventsData.count} {t('profile.eventsUnit')})
                      {hostedEventsVisibleNoRatingsBlock ? ` ${t('profile.noReviews')}` : ''}
                    </p>
                  ) : null
                )}
              </>
            )}

            {displayProfile.showEventRatings === 'YES' && (
              <section className="Profile-ratings-section" aria-labelledby="profile-arvostelut-heading">
                {hostedRatingsData == null || authoredEventsData == null ? (
                  <p className="Profile-ratings-empty">Ladataan…</p>
                ) : hasHostedEventsNoRatings ? (
                  <p className="Profile-ratings-empty">{t('profile.noReviews')}</p>
                ) : hostedRatingsData.totalRatings === 0 ? null : (
                  <>
                    <p className="Profile-ratings-summary">
                      Arvosteluja {hostedRatingsData.totalRatings}. KA: {hostedRatingsData.averageRating} / 5
                    </p>
                    <ul className="Profile-ratings-comments-list">
                      {hostedRatingsData.ratings.map((r, i) => (
                        <li key={i} className="Profile-rating-comment">
                          <RatingCommentRow
                            authorName={r.authorName}
                            authorBubbleColor={r.authorBubbleColor}
                            reason={r.reason}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>
            )}
            </div>
            {ignoreModalOpen && (
              <div className="FriendIgnoreModal-overlay" role="dialog" aria-modal="true" aria-labelledby="friend-ignore-title">
                <div className="FriendIgnoreModal">
                  <h2 id="friend-ignore-title" className="FriendIgnoreModal-title">
                    Ohituksen syy
                  </h2>
                  <p className="FriendIgnoreModal-hint">Kirjoita lyhyt syy (pakollinen).</p>
                  <textarea
                    className="FriendIgnoreModal-textarea"
                    rows={4}
                    value={ignoreReasonDraft}
                    onChange={(e) => setIgnoreReasonDraft(e.target.value)}
                    disabled={friendActionBusy}
                  />
                  <div className="FriendIgnoreModal-actions">
                    <button
                      type="button"
                      className="Button Button-secondary"
                      onClick={() => {
                        setIgnoreModalOpen(false);
                        setIgnoreReasonDraft('');
                      }}
                      disabled={friendActionBusy}
                    >
                      Peruuta
                    </button>
                    <button
                      type="button"
                      className="Button"
                      onClick={submitIncomingIgnore}
                      disabled={friendActionBusy || !ignoreReasonDraft.trim()}
                    >
                      Tallenna ohitus
                    </button>
                  </div>
                </div>
              </div>
            )}
            {isDmOpen && (
              <div className="DmSendView-overlay">
                <div className="DmSendView">
                  <div className="DmSendView-field">
                    <label>Keneltä:</label>
                    <span>{user?.displayName || user?.username || '—'}</span>
                  </div>
                  <div className="DmSendView-field">
                    <label>Kenelle:</label>
                    <span>{owner?.displayName || owner?.username || displayName}</span>
                  </div>
                  <div className="DmSendView-field DmSendView-field--message">
                    <label htmlFor="dm-message-input">Viesti:</label>
                    <textarea
                      id="dm-message-input"
                      rows={4}
                      value={dmMessage}
                      onChange={(e) => setDmMessage(e.target.value)}
                    />
                  </div>
                  {dmStatus && (
                    <p
                      className={`DmSendView-status${
                        dmStatus.variant ? ` DmSendView-status--${dmStatus.variant}` : ''
                      }`}
                    >
                      {dmStatus.message}
                    </p>
                  )}
                  <div className="DmSendView-actions">
                    <button
                      type="button"
                      className="Button"
                      onClick={handleCancelDm}
                      disabled={dmSending}
                    >
                      Peruuta
                    </button>
                    <button
                      type="button"
                      className="Button"
                      onClick={handleSendDm}
                      disabled={dmSending || !dmMessage.trim()}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

